import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Star } from 'lucide-react';
import { api } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const NAMES = { olaparty: 'Olaparty', trumeet: 'Trumeet' };
const INTERVAL = 4000;

export const HostBannerCarousel = ({ onCount, bare = false }) => {
    const [items, setItems] = useState([]);
    const [i, setI] = useState(0);
    const [paused, setPaused] = useState(false);
    const touch = useRef(null);

    useEffect(() => { api.get('/banners').then(r => { setItems(r.data.banners || []); onCount?.((r.data.banners || []).length); }).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const go = useCallback((d) => setI(p => (items.length ? (p + d + items.length) % items.length : 0)), [items.length]);
    useEffect(() => {
        if (paused || items.length < 2) return undefined;
        const t = setInterval(() => go(1), INTERVAL);
        return () => clearInterval(t);
    }, [paused, items.length, go]);

    if (!items.length) return null;

    const open = (b) => {
        if (!b.link) return;
        api.post(`/banners/${b.id}/click`).catch(() => {});
        window.open(b.link, '_blank', 'noopener,noreferrer');
    };
    const onTouchEnd = (e) => {
        if (touch.current == null) return;
        const dx = e.changedTouches[0].clientX - touch.current;
        touch.current = null;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    };

    return (
        <section className={bare ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10'} data-testid={bare ? 'dashboard-banner-carousel' : 'host-banner-carousel'}>
            {!bare && <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                <Star className="h-3.5 w-3.5 text-amber-500" /> Featured
            </div>}
            <div className="relative overflow-hidden rounded-2xl border border-border shadow-sm bg-[#0E2A47] select-none"
                onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
                onTouchStart={(e) => { touch.current = e.touches[0].clientX; setPaused(true); }}
                onTouchEnd={(e) => { onTouchEnd(e); setPaused(false); }}>
                <div className="flex transition-transform duration-700 ease-[cubic-bezier(.22,.8,.25,1)]" style={{ transform: `translateX(-${i * 100}%)` }}>
                    {items.map((b, idx) => (
                        <button key={b.id} type="button" onClick={() => open(b)} className={`relative w-full shrink-0 aspect-[12/5] text-left group focus:outline-none ${b.link ? '' : 'cursor-default'}`}
                            aria-label={b.link ? `Open ${b.host_name || b.in_app_id || 'banner'} link` : (b.host_name || 'Featured banner')} data-testid={`host-banner-slide-${idx}`}>
                            <img src={`${API}${b.image_url}`} alt={b.host_name || (b.in_app_id ? `Host ${b.in_app_id}` : 'Featured banner')} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.03]" loading={idx === 0 ? 'eager' : 'lazy'} draggable={false} />
                            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />
                            <div className="absolute left-3 right-3 bottom-3 sm:left-5 sm:bottom-5 flex items-end justify-between gap-3">
                                <div className="min-w-0 text-white">
                                    {b.platform && <div className="inline-flex items-center rounded-full bg-white/15 backdrop-blur-md px-2.5 py-0.5 text-[10px] sm:text-xs font-medium">{NAMES[b.platform] || b.platform}</div>}
                                    {b.host_name && <div className="mt-1 font-display text-base sm:text-2xl font-semibold truncate">{b.host_name}</div>}
                                    {b.in_app_id && <div className="text-[11px] sm:text-sm text-white/85 font-mono" data-testid={`host-banner-id-${idx}`}>ID: {b.in_app_id}</div>}
                                </div>
                                {b.link && <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white text-[#0E2A47] px-3 py-1.5 text-xs sm:text-sm font-semibold shadow transition-transform group-hover:-translate-y-0.5">
                                    View profile <ExternalLink className="h-3.5 w-3.5" />
                                </span>}
                            </div>
                        </button>
                    ))}
                </div>
                {items.length > 1 && (
                    <>
                        <button type="button" onClick={() => go(-1)} className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full bg-white/80 hover:bg-white transition-colors" aria-label="Previous" data-testid="host-banner-prev"><ChevronLeft className="h-5 w-5" /></button>
                        <button type="button" onClick={() => go(1)} className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 items-center justify-center rounded-full bg-white/80 hover:bg-white transition-colors" aria-label="Next" data-testid="host-banner-next"><ChevronRight className="h-5 w-5" /></button>
                        <div className="absolute top-3 right-3 flex gap-1.5 rounded-full bg-black/35 backdrop-blur-sm px-2 py-1.5">
                            {items.map((b, idx) => (
                                <button key={b.id} type="button" onClick={() => setI(idx)} aria-label={`Slide ${idx + 1}`}
                                    className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${idx === i ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`} data-testid={`host-banner-dot-${idx}`} />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};
